import { keepPreviousQueryData, useInfiniteQuery } from '@northladder/services';

import { BIDS_API } from '../bidsAPIClient';
import { IFetchAllUploadedBidsPayload } from '../types';

/**
 * This is to track the uploaded bids listing key in react query cache.
 */
export const FETCH_ALL_UPLOADED_BIDS_KEY = 'fetch-all-uploaded-bids-key';

/**
 * This performs a get request and retrieves the uploaded bids data by category and
 * brands.
 * Provides data fetching states to be used in the uploaded bids list.
 */
export const useSearchUploadedBidsQuery = (
  payload: IFetchAllUploadedBidsPayload
) => {
  const {
    bidValidityConfigId = '',
    brandIds = [],
    categoryId = '',
    count,
    groupIds = [],
    isInstantBiddingEnabled = false,
    page,
    searchText = '',
    status = '',
  } = payload;

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPaused,
    isPending,
    refetch,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      FETCH_ALL_UPLOADED_BIDS_KEY,
      bidValidityConfigId,
      brandIds,
      categoryId,
      count,
      groupIds,
      isInstantBiddingEnabled,
      page,
      searchText,
      status,
    ],
    networkMode: 'always', // Even make calls when offline
    queryFn: ({ pageParam }) =>
      BIDS_API.searchAllUploadedBids({ ...payload, page: pageParam }),
    initialPageParam: page,
    placeholderData: keepPreviousQueryData,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const currentCount = allPages.length * count;

      return currentCount < lastPage.totalCount ? nextPage : undefined;
    },
  });

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPaused,
    isPending,
    refetch,
  };
};
